import { useMutation } from "@tanstack/react-query";
import type { AddressResolutionReason, AddressResolutionStatus } from "@beztack/delivery";
import { ApiError, requestJson } from "@/lib/api-client";

export type { AddressResolutionReason, AddressResolutionStatus } from "@beztack/delivery";

// =============================================================================
// Issue #66: backend-owned Address resolution hook (inline path).
// Issue #68: same hook now also accepts a saved `customerAddressId` so the
//   saved Customer Address can converge into the same backend token flow
//   as inline addresses. The frontend never invents an Address resolution
//   token; it always asks the backend to evaluate the address against
//   the Organization's current Delivery configuration.
//
// Exactly one of `input` (inline) or `customerAddressId` (saved) must be
// supplied on each mutation. The hook never resolves both at once.
// =============================================================================

export type InlineDeliveryAddressInput = {
  label?: string | null;
  line1: string;
  line2?: string | null;
  city?: string | null;
  country?: string | null;
  lat: number;
  lng: number;
  notes?: string | null;
};

export type SavedDeliveryAddressResolveInput = {
  customerAddressId: string;
};

export type ResolveDeliveryAddressVariables =
  | { input: InlineDeliveryAddressInput; customerAddressId?: never }
  | { customerAddressId: string; input?: never };

export type AddressResolutionResult = {
  status: AddressResolutionStatus;
  reason: AddressResolutionReason;
  deliverable: boolean;
  normalized: {
    line1: string;
    city: string | null;
    country: string;
    lat: number;
    lng: number;
  } | null;
  precision: "exact" | "approximate" | "area";
  candidates: Array<{ line1: string; lat: number; lng: number }>;
  distanceMeters: number | null;
  token: string | null;
  tokenExpiresAt: string | null;
  // Issue #68: present when the resolver consumed a saved Customer
  // Address (so the Order route can keep the Order linked). Null for
  // inline resolutions and for deleted-address fallbacks.
  customerAddressId: string | null;
  providerName: string | null;
  resolutionId: string | null;
  evidence: Record<string, unknown> | null;
};

export function useResolveDeliveryAddress(orgSlug: string | undefined) {
  return useMutation<AddressResolutionResult, ApiError, ResolveDeliveryAddressVariables>({
    mutationFn: (variables) => {
      if (!orgSlug && "customerAddressId" in variables) {
        return Promise.reject(new Error("Missing organization slug"));
      }
      const body =
        "input" in variables && variables.input
          ? orgSlug
            ? { input: variables.input, orgSlug }
            : { input: variables.input }
          : { customerAddressId: variables.customerAddressId, orgSlug };
      return requestJson<AddressResolutionResult>("/api/orders/delivery-address-resolve", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(body),
      });
    },
  });
}
